'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { sendPriceAnalytics } from 'prices/analytics/sendPriceAnalytics'
import { getShouldShowExtraInfo } from 'prices/utils/getShouldShowExtraInfo/getShouldShowExtraInfo'
import { useRef } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { Channel } from 'types/masterData'
import { usePricesStore } from '../../context/PricesProvider'
import type { Price } from '../../store/pricesStore/pricesStore'
import { SheetPricesHistory } from './SheetPricesHistory/SheetPricesHistory'
import { TooltipPricesHistory } from './TooltipPricesHistory/TooltipPricesHistory'

export type ExtraInfoProps = {
	readonly pricesHistory: Price[]
	readonly shouldShowDiscount: boolean
	readonly crossedOut: Price[]
}

export type PricesHistoryComponentProps = {
	readonly pricesHistory: Price[]
	readonly shouldShowDiscount: boolean
	readonly sendAnalytics: () => void
}

export function ExtraInfo({
	pricesHistory,
	shouldShowDiscount,
	crossedOut,
}: ExtraInfoProps) {
	const { isSmall } = useResponsive()
	const {
		showDiscountInfo = false,
		hideSaleOrPromoPrice,
		channel,
		country,
	} = usePricesStore()
	const { isApp } = useMasterData()
	const analyticsSent = useRef(false)
	const { hasPriceHistory } = country

	if (isApp) {
		return null
	}

	const showExtraInfo = hideSaleOrPromoPrice || showDiscountInfo

	const shouldShowExtraInfo = getShouldShowExtraInfo({
		channel,
		hasPriceHistory,
		showExtraInfo,
		shouldShowDiscount,
		crossedOut: crossedOut.map((price) => price.amount),
	})

	if (!shouldShowExtraInfo) {
		return null
	}

	const isHightlighted: boolean = channel === Channel.Outlet

	const sendAnalytics = () => {
		if (!analyticsSent.current) {
			analyticsSent.current = true
			sendPriceAnalytics(
				pricesHistory.map((price) => price.amount),
				hideSaleOrPromoPrice,
				isHightlighted,
				channel
			)
		}
	}

	const pricesHistoryProps: PricesHistoryComponentProps = {
		pricesHistory,
		shouldShowDiscount,
		sendAnalytics,
	}

	return isSmall ? (
		<SheetPricesHistory {...pricesHistoryProps} />
	) : (
		<TooltipPricesHistory {...pricesHistoryProps} />
	)
}
